/**
 * The viewer's inbox of Space invites addressed to them, with accept/decline.
 *
 * The list always comes from `GET /api/spaces/invites`, never from a socket
 * payload. Invite events are only a hint to refetch: each row names a Space the
 * recipient has not joined yet, so the authorized endpoint stays the single
 * source of what they are allowed to see.
 *
 * Accepting a `cipherRequired` Space solves the challenge locally and sends
 * only the resulting plaintext. The Community Cipher never leaves the device —
 * the invite carries the public challenge precisely so the proof can be built
 * without fetching a hidden Space.
 *
 * @module hooks/spaces/useSpaceInvites
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createApiClient, type PublicSpaceInvite, type SpaceInvitePreview } from '@adieuu/shared';
import { useAppConfig } from '../../config';
import { useIdentity } from '../useIdentity';
import { useCipherStore } from '../useCipherStore';
import { detectSpaceCipher, solveSpaceCipherCheck } from '../../services/spaceCipherService';
import {
  emitSpacesChanged,
  onSpaceInvitesChanged,
} from '../../services/spacesMembershipEvents';

export interface AcceptSpaceInviteOutcome {
  ok: boolean;
  /** Slug to route to, when the accept produced a joinable Space. */
  slug?: string;
  /** Set when the invite needs a Community Cipher the viewer does not hold. */
  needsCipher?: boolean;
  error?: string;
}

export function useSpaceInvites() {
  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const { status: identityStatus } = useIdentity();
  const { ciphers, getCipherKey, encryptionAvailable } = useCipherStore();

  const [invites, setInvites] = useState<PublicSpaceInvite[]>([]);
  const [loading, setLoading] = useState(false);

  const isLoggedIn = identityStatus === 'logged_in';

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setInvites([]);
      return;
    }
    setLoading(true);
    const res = await api.spaces.listInvites(50);
    setInvites(res.success && res.data ? res.data.invites : []);
    setLoading(false);
  }, [api, isLoggedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => onSpaceInvitesChanged(() => void refresh()), [refresh]);

  /**
   * Build the join proof for an invite, or report that it cannot be built.
   *
   * Returns `undefined` when no proof is needed. `needsCipher` means the viewer
   * holds no Cipher that decrypts this Space's challenge, which is a local
   * verdict — the server would refuse the accept anyway.
   */
  const buildProof = useCallback(
    async (
      invite: PublicSpaceInvite,
    ): Promise<{ proof?: { knownValue: string } } | { needsCipher: true }> => {
      if (!invite.cipherRequired || !invite.cipherCheck) return {};
      if (!encryptionAvailable || ciphers.length === 0) return { needsCipher: true };

      const candidates = ciphers
        .map((c) => getCipherKey(c.id))
        .filter((c): c is NonNullable<typeof c> => !!c);
      const found = await detectSpaceCipher(candidates, invite.spaceId, invite.cipherCheck);
      if (!found) return { needsCipher: true };

      const knownValue = await solveSpaceCipherCheck(found, invite.spaceId, invite.cipherCheck);
      if (!knownValue) return { needsCipher: true };
      return { proof: { knownValue } };
    },
    [ciphers, encryptionAvailable, getCipherKey],
  );

  const accept = useCallback(
    async (inviteId: string): Promise<AcceptSpaceInviteOutcome> => {
      const invite = invites.find((i) => i.id === inviteId);
      if (!invite) return { ok: false };

      const built = await buildProof(invite);
      if ('needsCipher' in built) return { ok: false, needsCipher: true };

      const res = await api.spaces.acceptInvite(inviteId, built.proof);
      if (!res.success) {
        return { ok: false, ...(res.error?.message ? { error: res.error.message } : {}) };
      }

      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      emitSpacesChanged();
      return { ok: true, ...(invite.spaceSlug ? { slug: invite.spaceSlug } : {}) };
    },
    [api, buildProof, invites],
  );

  const decline = useCallback(
    async (inviteId: string): Promise<boolean> => {
      const res = await api.spaces.declineInvite(inviteId);
      if (res.success) {
        setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      }
      return res.success;
    },
    [api],
  );

  const getInvitePreview = useCallback(
    async (inviteId: string): Promise<SpaceInvitePreview | null> => {
      const res = await api.spaces.getInvitePreview(inviteId);
      return res.success && res.data ? res.data : null;
    },
    [api],
  );

  return { invites, loading, refresh, accept, decline, getInvitePreview };
}
